import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle2, Eye, EyeOff, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import api, { fmtErr } from "../lib/api";
import BrandLogo from "../components/BrandLogo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (password !== confirm) { toast.error("Passwords do not match"); return; }
    setBusy(true);
    setError("");
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
    } catch (err) {
      setError(fmtErr(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC] p-6" data-testid="reset-password-page">
      <div className="w-full max-w-sm bg-white border border-slate-200 rounded-2xl p-8 space-y-6 shadow-sm">
        <div className="flex items-center gap-2">
          <BrandLogo />
          <span className="font-heading font-bold text-lg text-slate-900">MatteusMx</span>
        </div>

        {done ? (
          <div className="space-y-4" data-testid="reset-success">
            <div className="h-11 w-11 rounded-full bg-emerald-50 flex items-center justify-center">
              <CheckCircle2 className="h-5 w-5 text-emerald-600" />
            </div>
            <h1 className="font-heading text-xl font-bold text-slate-900">Password updated</h1>
            <p className="text-sm text-slate-500">Your password has been changed. All previous sessions were signed out — sign in with your new password.</p>
            <Link to="/">
              <Button className="w-full bg-slate-900 hover:bg-slate-800" data-testid="reset-go-login-btn">Go to sign in</Button>
            </Link>
          </div>
        ) : !token || error ? (
          <div className="space-y-4" data-testid="reset-error">
            <div className="h-11 w-11 rounded-full bg-red-50 flex items-center justify-center">
              <ShieldAlert className="h-5 w-5 text-red-500" />
            </div>
            <h1 className="font-heading text-xl font-bold text-slate-900">Link not usable</h1>
            <p className="text-sm text-slate-500">{error || "This reset link is missing its token. Please request a new one."}</p>
            <Link to="/forgot-password">
              <Button variant="outline" className="w-full" data-testid="reset-request-new-btn">Request a new reset link</Button>
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5">
              <h1 className="font-heading text-xl font-bold text-slate-900">Set a new password</h1>
              <p className="text-sm text-slate-500">Minimum 6 characters. This link works once and expires 1 hour after it was sent.</p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="rp-password">New password</Label>
              <div className="relative">
                <Input id="rp-password" type={showPw ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)}
                  required className="h-11 pr-10" data-testid="reset-password-input" autoComplete="new-password" />
                <button type="button" onClick={() => setShowPw(!showPw)} data-testid="reset-toggle-password-btn"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 transition-colors">
                  {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="rp-confirm">Confirm new password</Label>
              <Input id="rp-confirm" type={showPw ? "text" : "password"} value={confirm} onChange={(e) => setConfirm(e.target.value)}
                required className="h-11" data-testid="reset-confirm-input" autoComplete="new-password" />
            </div>
            <Button type="submit" className="w-full h-11 bg-slate-900 hover:bg-slate-800" disabled={busy} data-testid="reset-submit-btn">
              {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />} Update password
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
